import { useState } from 'react';
import { Modal, Stack, Text, Select, Button, Group } from '@mantine/core';
import { IconUserMinus } from '@tabler/icons-react';
import type { Asset } from '@/types/models';

interface UnassignAssetModalProps {
  asset: Asset | null;
  opened: boolean;
  onClose: () => void;
  onConfirm: (assetId: string, newStatus: 'Available' | 'Maintenance' | 'Retired' | 'Lost') => void;
  loading?: boolean;
}

export function UnassignAssetModal({
  asset,
  opened,
  onClose,
  onConfirm,
  loading = false,
}: UnassignAssetModalProps) {
  const [selectedStatus, setSelectedStatus] = useState<string>('Available');

  const handleConfirm = () => {
    if (!asset) return;
    onConfirm(asset.id, selectedStatus as 'Available' | 'Maintenance' | 'Retired' | 'Lost');
  };

  const handleClose = () => {
    setSelectedStatus('Available');
    onClose();
  };

  if (!asset) return null;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Unassign Asset"
      size="sm"
    >
      <Stack gap="md">
        <Text size="sm">
          Unassign <strong>{asset.description || asset.tag_id}</strong> from the current person?
        </Text>

        <Select
          label="Set status after unassigning"
          description="What should the asset status be after unassigning?"
          value={selectedStatus}
          onChange={(val) => setSelectedStatus(val || 'Available')}
          data={[
            { value: 'Available', label: 'Available - Ready for use' },
            { value: 'Maintenance', label: 'Maintenance - Needs repair/service' },
            { value: 'Retired', label: 'Retired - No longer in service' },
            { value: 'Lost', label: 'Lost - Cannot be located' },
          ]}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            color="orange"
            leftSection={<IconUserMinus size={16} />}
            onClick={handleConfirm}
            loading={loading}
          >
            Unassign
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
